import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Button, Menu, MenuItem } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import dayjs from 'dayjs';
import { useState } from 'react';
import Swal from 'sweetalert2';
import * as yup from 'yup';
import strings from '../Globalization/index';
import consts from '../consts';
import Model from '../models/model';
import connection from '../services/Connection';
import util from '../util';
import CatAdminForm from '../views/forms/CatAdminForm';
import { dataStore } from '../Store';
import { useGridBase } from '../components/grid/GridBaseContext';
import { syncFieldTests , fetchUserSyncDeviceType, userLogColumns } from './UserSyncTable';

const requiredRole = ['supervisor', 'steris specialist', 'operator', 'physician admin'];
const isNeedToShow = () => {
	const { auth } = dataStore.getState()
	const RoleName = auth?.tags?.RoleName;
	return !requiredRole.includes(RoleName?.toLowerCase())
}
const filter = [{ "operator": "$or", "property": "UserType", "value": 'Physician', type: 'string' }, { "operator": "$or", "property": "UserType", "value": 'Assistant', type: 'string' }];
const { selectedUserSyncDeviceType, PhysicianUserType, AssistantUserType } = consts

const { isGridReadOnly, viewOnly, canAdd, canModify, deviceTypeRenderr, isFromTrackingSystem } = util;

const readOnlyCheck = ({ formik }) => {
	return Boolean(formik?.values?.Id);
}

const onNewHideColumn = ({ formik }) => {
	return Boolean(formik?.values?.Id);

}

const CustomCheckBox = ({ id, row, value }) => {
	const { rowSelectionModel, onRowSelectionModel } = useGridBase();
	const handleOnClick = () => {
		const result = Array.from(rowSelectionModel.ids).find(item => item === row._id);
		if (result) {
			const updatedRowSelectionModel = Array.from(rowSelectionModel.ids).filter(item => item !== row._id);
			rowSelectionModel.ids = new Set(updatedRowSelectionModel);
			onRowSelectionModel(rowSelectionModel);
		}
		else {
			rowSelectionModel.ids.add(row._id);
			onRowSelectionModel(rowSelectionModel);
		}
	}

	const handleChecked = () => {
		return Array.from(rowSelectionModel.ids).some(item => item === row._id);
	}
	return (
		<Checkbox
			checked={handleChecked()}
			onClick={handleOnClick}
			disabled={viewOnly()}
			color="primary"
			value={row?.id}
			inputProps={{ 'aria-label': 'checkbox' }}
		/>
	)
}


const onExportToURSX = async (name, extension, type, data) => {
	let { rows } = data;
	rows = rows.filter(row => row.UserType === PhysicianUserType || row.UserType === AssistantUserType);
	const fileName = `${name}_${dayjs().format(consts.CantelTimeFormat)}${extension}`;
	const formValue = new FormData();
	let response;
	formValue.append("action", 'otherAction');
	formValue.append('filter', JSON.stringify(filter))
	formValue.append('isFromEndoSetupPhysician', true);
	if (rows.length !== 0) {
		if (extension === '.XSRX') {
			formValue.append('exportFormat', extension);
			formValue.append('exportType', type);
			formValue.append("otherAction", 'HandelSRXExport');
		} else if (extension === '.csv') {
			formValue.append('exportFormat', extension);
			formValue.append('exportType', type);
			formValue.append("otherAction", 'HandelXLSXExport');
		}
		try {
			response = await fetch(`${connection.UserSync}`, {
				method: 'POST',
				body: formValue
			});
			response.blob().then((b) => {
				let a = document.createElement("a");
				a.href = URL.createObjectURL(b);
				a.setAttribute("download", fileName);
				a.click();
			});
		} catch (err) {
			const result = await response.json();
			if (result?.info || !result?.success) {
				Swal.fire({
					title: "Error",
					text: strings[result?.info],
					icon: "error",
					button: strings.Ok,
					allowOutsideClick: false
				})
			}
		}
	} else {
		Swal.fire({
			title: strings.Error,
			text: strings.noRecordFound,
			icon: 'error',
			button: strings.Ok,
			allowOutsideClick: false
		})
	}
};

const CombinedExport = ({ data, onExport }) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const handleOnExport = async (type) => {
		await onExport(type)
	}
	return (
		<div>
			<Button
				id="fade-button"
				aria-controls={open ? 'fade-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				onClick={handleClick}
				variant='contained'
				disabled={viewOnly()}

			>
				<FileDownloadOutlinedIcon />
				{strings.Export}
			</Button>
			<Menu
				id="fade-menu"
				MenuListProps={{
					'aria-labelledby': 'fade-button',
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
			>
				<MenuItem onClick={() => handleOnExport('XLSX')}>{strings.UserExcelGrid}</MenuItem>
				<MenuItem onClick={() => handleOnExport('CSV')}>{strings.UserCsvGrid}</MenuItem>
				<MenuItem onClick={() => handleOnExport('PDF')}>{strings.UserPdfGrid}</MenuItem>
			</Menu>
		</div>
	)
}

const defaultUsername = ({ formik, values }) => {
	const { setFieldValue } = formik;
	if ((!formik.touched.UserName || values.UserName === '') && !values.id) {
		setFieldValue('UserName', `${values?.FirstName || ''}${values?.LastName || ''}`);
		if (values.UserName === '' && formik.touched.UserName) {
			formik.setFieldTouched('UserName', false)
		}
	}
}

const fetchEndoUserType = async () => {
	const formData = new FormData();
	formData.append('comboType', "EndoUserType");
	const { options, count } = await fetch(connection.Combo, {
		method: 'POST',
		body: formData
	}).then((res) => res.json()).then((json) => {
		const { records } = json;
		let options = records?.map((record) => ({
			id: record.LookupId,
			label: record.DisplayValue
		}));
		if (!Array.isArray(options)) {
			options = [];
		}
		// Filtering only physicians and assistant Endo user types
		const filteredOptions = options.filter((option) => option.label.toLowerCase() === "physician" || option.label.toLowerCase() === "assistant")
		return { options: filteredOptions || [], count: filteredOptions?.length };
	});
	return { options, count };

}



const model = new Model({
	name: 'EndoSetupPhysician',
	title: 'EndoSetupPhysician',
	defaultSort: 'UserName ASC',
	disableAdd: canAdd,
	readOnly: isGridReadOnly,
	disableDelete: true,
	allowImport: canModify,
	disablePrintBarcode: false,
	disableSelectAll: false,
	idProperty: '_id',
	isAddRemove: false,
	disableExport: false,
	customeErrorOnSubmit: true,
	showGridRefreshBtn: true,
	apiParams: { "isFromEndoSetupPhysician": true, endoSetupPhysicianFilter: JSON.stringify(filter) },
	importProps: { message: 'UserCountWarning', maxCount: consts.maxUserCount, logColumns: userLogColumns, fileType: consts.endoImportSpec.Physicians.fileType, type: consts.endoImportSpec.Physicians.type },
	FormExtension: ({ formik, activeRecord, modelConfig, isNew }) => {
		return (
			<CatAdminForm activeRecord={activeRecord} formik={formik} modelConfig={modelConfig}
				isItemSync={false}
				isNew={isNew}
			/>
		)
	},
	formGroups: [{
		fields: ["CreatedOn", "ModifiedOn", "LastResyncOn", "DataSource", "Codes",
			"UserUID", "UserName", "FirstName", "LastName", "UserType",
			"IsActive", "ReSync", "InterFacedYN", "EquipmentName", "DeviceTypes", "UserCodes"]
	}],
	customizeSaveData: (toSave, { values, activeRecord }) => {
		toSave.RemoveCodes = JSON.stringify(values?.RemoveCodes || []);
		toSave.Parameters = JSON.stringify(activeRecord?.record?.Parameters || []);
		toSave.UserCodes = JSON.stringify(values?.UserCodes || [])
		toSave.isFromEndoSetupPhysician = true;
		return toSave;
	},
	gridTitle: 'EndoSetupPhysician',
	columns: [
		{ header: 'UserId', field: 'UserUID', width: 120, hidden: true, readOnly: readOnlyCheck, isNotformfield: true },
		{ field: 'UserName', width: 120, required: true, inputProps: { autoFocus: false }, readOnly: isFromTrackingSystem, validate: yup.string().test(syncFieldTests.PhysicianUsernameLengthMax20) },
		{ field: 'FirstName', width: 120, onChange: defaultUsername, eager: true, readOnly: isFromTrackingSystem },
		{ field: 'LastName', width: 120, onChange: defaultUsername, eager: true, readOnly: isFromTrackingSystem },
		{ field: 'CodesCount', header: 'BarcodeCount', width: 120, isNotformfield: true, type: 'string', readOnly: isFromTrackingSystem, hidden: true },
		{ field: 'UserType', width: 120, lookup: fetchEndoUserType, readOnly: isFromTrackingSystem, required: true, defaultValue: "Physician" },
		{ header: 'Active', field: 'IsActive', type: 'bool', defaultValue: true, width: 120, readOnly: isFromTrackingSystem, onChange: ({ formik, value }) => { formik.setFieldValue('ReSync', value) } },
		{ header: 'DateDeActivated', field: 'DateDeActivated', type: 'date', width: 120, isLocal: true, format: 'MM/DD/YYYY hh:mm:ss A', hidden: true, readOnly: isFromTrackingSystem },
		{ field: 'ReSync', type: 'bool', width: 100, hidden: true, inputWhen: onNewHideColumn, readOnly: isFromTrackingSystem, onChange: ({ formik, value }) => { formik.setFieldValue('IsActive', value) }, defaultValue: true },
		{ field: 'DataSource', width: 220, readOnly: true, inputWhen: onNewHideColumn, hidden: true },
		{ header: 'DefaultBarcode', field: 'DefaultBarcode', width: 150 },
		{ header: 'DefaultRFID', field: 'DefaultRFID', hidden: true, width: 150 },
		{ header: 'updatedAt', field: 'ModifiedOn', width: 200, type: 'date', readOnly: true, format: 'MM/DD/YYYY hh:mm:ss A', inputWhen: onNewHideColumn, isLocal: true },
		{ header: 'Created', field: 'CreatedOn', width: 200, type: 'date', format: 'MM/DD/YYYY hh:mm:ss A', hidden: true, readOnly: true, inputWhen: onNewHideColumn, isLocal: true },
		{ header: 'LastResyncOn', field: 'LastResyncOn', width: 120, type: 'date', hidden: true, readOnly: true, format: 'MM/DD/YYYY hh:mm:ss A', inputWhen: onNewHideColumn, isLocal: true },
		{
			header: 'Codes', field: 'UserCodes', width: 100, hidden: true,
			validate: yup.array().of(
				yup.object().shape({
					CodeType: yup.string().required(),
					Code: yup.string(),
					IsActive: yup.boolean(),
					DefaultValue: yup.boolean(),
					RFIDTagNumber: yup.string(),
					RFIDEquipmentID: yup.string()
				})
			)
				.test(syncFieldTests.DuplicateBarcodeFound)
				.test(syncFieldTests.AtLeastOneBarcodeisRequired)
				.test(syncFieldTests.AtLeastOneDefaultBardcodeisRequired)
				.test(syncFieldTests.OverDefaultBarcodeFound)
				.test(syncFieldTests.CodeisRequired)
				.test(syncFieldTests.CodeLengthMax100)
				.test(syncFieldTests.DuplicateISARFIDFound)
				.test(syncFieldTests.DuplicateRapidRFIDTagFound),
			excludeOnGrid: true,
			isNotExportField: true,
			isNotformfield: true
		},
		{
			header: 'Parameters', field: 'Parameters', width: 220, hidden: true, lookup: 'UserSyncParameters', type: 'object', validate: yup.array().of(
				yup.object().shape({
					Key: yup.string().required(),
					Value: yup.string(),
				})
			),
			excludeOnGrid: true,
		},
		{ header: 'DeviceTypes', field: 'DeviceTypes', hidden: true, width: 120, lookup: fetchUserSyncDeviceType, defaultValue: selectedUserSyncDeviceType, multiple: true, type: 'object', valueGetter: deviceTypeRenderr, readOnly: isFromTrackingSystem, inputWhen: isNeedToShow },
		{ header: 'PrintBarcode', field: 'Print', type: 'action', filterable: false, width: 120, renderCell: CustomCheckBox, isNotExportField: true },

	],
	extraExportConfig: {
		PDF: {
			pageSize: 'A2'
		}
	}
});

export default model.createFormBase();
